import React, { InputHTMLAttributes } from 'react';
import { Input, InputStateProps } from '../Input/Input';
import { PasswordInputProps } from '../PasswordInput/PasswordInput';
import { SchoolDomainInputProps } from '../SchoolDomainInput/SchoolDomainInput';
import { AdditionalPhoneNumberInputProps } from '../../PhoneNumberInput/PhoneNumberInput';
import HelpIconButton from '../HelpIconButton/HelpIconButton';

type AdditionalInputPropTypes = PasswordInputProps | SchoolDomainInputProps | AdditionalPhoneNumberInputProps;

export interface InputFormGroupProps extends InputHTMLAttributes<HTMLInputElement>, InputStateProps {
  label: string;
  helperText?: string;
  inputClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  helperClassName?: string;
  inputComponent?: React.ElementType;
  showRequiredFieldIndicators?: boolean;
  additionalInputProps?: AdditionalInputPropTypes;
  icon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  helpIconId?: string;
  passwordProfile?: string;
  initialValue?: string;
}

function InputFormGroupBase({
  children,
  label,
  name,
  id,
  type,
  value,
  helperText,
  labelClassName,
  inputClassName,
  errorClassName,
  helperClassName,
  autoComplete,
  'aria-required': ariaRequired,
  error,
  dirty,
  touched,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  placeholder,
  disabled,
  inputComponent,
  showRequiredFieldIndicators,
  additionalInputProps,
  icon,
  helpIconId,
  initialValue,
}: InputFormGroupProps) {
  const InputComponent = inputComponent || Input;

  const shouldDisplayErrors = !!(error && touched && dirty);

  const errorMessageBoxID = `input-error-${name}`;
  const helperMessageBoxID = `input-helper-${name}`;
  const passwordMessageBoxID = 'passwordRequirements';
  const errorMessageBoxAriaLive = (shouldDisplayErrors && 'polite') || 'off';
  const passwordHelperMessage = name === 'password' && !shouldDisplayErrors;

  const showInput = () => {
    if (icon) {
      return (
        <div className="pos-rel">
          <InputComponent
            name={name}
            id={id}
            type={type}
            value={value}
            className={shouldDisplayErrors ? `${inputClassName} invalid` : inputClassName}
            autoComplete={autoComplete}
            aria-required={ariaRequired}
            hasError={shouldDisplayErrors}
            aria-describedby={(passwordHelperMessage && passwordMessageBoxID)
              || (shouldDisplayErrors && errorMessageBoxID)
              || (helperText && helperMessageBoxID)}
            aria-invalid={shouldDisplayErrors}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            initialValue={initialValue}
            {...additionalInputProps}
          />
          <div className="input-icon-container">{icon}</div>
        </div>
      );
    }
    return (
      <InputComponent
        name={name}
        id={id}
        type={type}
        value={value}
        className={shouldDisplayErrors ? `${inputClassName} invalid` : inputClassName}
        autoComplete={autoComplete}
        aria-required={ariaRequired}
        hasError={shouldDisplayErrors}
        aria-describedby={(passwordHelperMessage && passwordMessageBoxID)
          || (shouldDisplayErrors && errorMessageBoxID)
          || (helperText && helperMessageBoxID)}
        aria-invalid={shouldDisplayErrors}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        initialValue={initialValue}
        {...additionalInputProps}
      />
    );
  };

  return (
    <div className={`form-group${icon ? ' icon' : ''}`}>

      <label htmlFor={id} className={labelClassName} id={`label-for-${id}`}>
        {label + (showRequiredFieldIndicators ? ' *' : '')}
        {helpIconId && (
          <HelpIconButton helpIconId={helpIconId} />
        )}
      </label>
      {showInput()}
      <div id={helperMessageBoxID} className={helperClassName}>
        {helperText || null}
      </div>

      <div id={errorMessageBoxID} className={errorClassName} aria-live={errorMessageBoxAriaLive}>
        {shouldDisplayErrors ? error : null}
      </div>

      {children}
    </div>
  );
}

export const InputFormGroup = React.memo(InputFormGroupBase);
